// hooks/useDeliveryCalculator.ts
import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { CartItem, DeliveryRule } from "../../../types/types";
import { DeliveryType } from "../../../types/enums";
import { selectCurrentCluster } from "../../../../store/slices/currentClusterSlice";
import { selectStoreList } from "../../../../store/slices/storeListSlice";

interface UseDeliveryCalculatorProps {
  cartItems: CartItem[];
  deliveryAddress?: {
    latitude: number;
    longitude: number;
  } | null;
  deliveryType: DeliveryType;
}

export const useDeliveryCalculator = ({
  cartItems,
  deliveryAddress,
  deliveryType,
}: UseDeliveryCalculatorProps) => {
  const currentCluster = useSelector(selectCurrentCluster);
  const allStores = useSelector(selectStoreList);

  const [loading, setLoading] = useState(false);
  const [available, setAvailable] = useState(false);
  const [deliveryStoreId, setDeliveryStoreId] = useState<number | null>(null);
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const cartItemsKey = useMemo(() => {
    return cartItems.map((item) => `${item.id}:${item.quantity}`).join("|");
  }, [cartItems]);

  useEffect(() => {
    const calculateDelivery = async () => {
      setAvailable(false);
      setDeliveryStoreId(null);
      setDeliveryDistance(null);
      setDeliveryFee(0);
      setError(null);

      if (
        deliveryType !== DeliveryType.DELIVERY ||
        !deliveryAddress ||
        !currentCluster
      ) {
        return;
      }

      setLoading(true);

      try {
        const storesWithDelivery = allStores.filter((s) => s.withDelivery);

        if (!storesWithDelivery.length) {
          setError("Нет точек с доставкой");
          return;
        }

        let nearestStore: any = null;
        let minDistance = Infinity;

        for (const store of storesWithDelivery) {
          if (!store.address?.latitude || !store.address?.longitude) {
            continue;
          }

          const distance = calculateDistance(
            Number(store.address.latitude),
            Number(store.address.longitude),
            Number(deliveryAddress.latitude),
            Number(deliveryAddress.longitude),
          );

          if (isNaN(distance)) {
            continue;
          }

          const distanceMeters = distance * 1000;
          const withinStoreRadius =
            store.deliveryRadius === null ||
            distanceMeters <= store.deliveryRadius;

          if (!withinStoreRadius) continue;

          if (distance < minDistance) {
            minDistance = distance;
            nearestStore = { ...store, distance };
          }
        }

        if (!nearestStore) {
          setError("Доставка недоступна по этому адресу");
          return;
        }

        const itemsTotal = cartItems.reduce(
          (sum, item) => sum + item.price * item.quantity,
          0,
        );

        const store = nearestStore;
        const distanceMeters = store.distance * 1000;
        const allRules = (currentCluster.delivery as DeliveryRule[]) || [];

        const applicable = allRules
          .filter((r) => Number(r.radius) >= distanceMeters)
          .filter((r) => Number(r.level) <= itemsTotal);

        let price = 0;
        if (applicable.length) {
          price = Math.min(...applicable.map((r) => Number(r.value)));
        }

        setAvailable(true);
        setDeliveryStoreId(store.id ?? null);
        setDeliveryDistance(store.distance);
        setDeliveryFee(price);
      } catch (err) {
        setError("Ошибка расчета доставки");
      } finally {
        setLoading(false);
      }
    };

    calculateDelivery();
  }, [deliveryType, deliveryAddress, currentCluster, allStores, cartItemsKey]);

  return {
    available,
    deliveryStoreId,
    deliveryDistance,
    deliveryFee,
    loading,
    error,
  };
};

const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};
